import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Copy, ExternalLink, Search, Grid, List } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

const OSINT_CATEGORIES = [
  {
    title: "Subdomain Discovery",
    description: "Find subdomains for a target domain using automated tools and online services.",
    tools: [
      {
        name: "Sublist3r",
        command: "sublist3r -d example.com",
        link: "https://github.com/aboul3la/Sublist3r"
      },
      {
        name: "Amass",
        command: "amass enum -d example.com",
        link: "https://github.com/owasp-amass/amass"
      },
      {
        name: "crt.sh",
        command: "https://crt.sh/?q=example.com",
        link: "https://crt.sh/"
      }
    ]
  },
  {
    title: "Email Address Discovery",
    description: "Harvest email addresses from public sources.",
    tools: [
      {
        name: "theHarvester",
        command: "theHarvester -d example.com -l 500 -b google,bing,linkedin",
        link: "https://github.com/laramies/theHarvester"
      },
      {
        name: "Hunter.io",
        command: "https://hunter.io/search/example.com",
        link: "https://hunter.io/"
      }
    ]
  },
  {
    title: "Metadata Extraction",
    description: "Extract metadata from public documents.",
    tools: [
      {
        name: "metagoofil",
        command: "metagoofil -d example.com -t pdf,doc,docx -l 100 -n 50 -f results.html",
        link: "https://github.com/opsdisk/metagoofil"
      },
      {
        name: "exiftool",
        command: "exiftool *.pdf",
        link: "https://exiftool.org/"
      }
    ]
  },
  {
    title: "Social Media Enumeration",
    description: "Find social media accounts and mentions related to the target.",
    tools: [
      {
        name: "Sherlock",
        command: "sherlock username",
        link: "https://github.com/sherlock-project/sherlock"
      },
      {
        name: "Social-Searcher",
        command: "https://www.social-searcher.com/",
        link: "https://www.social-searcher.com/"
      }
    ]
  },
  {
    title: "Shodan & Censys",
    description: "Search for exposed devices and services using Shodan and Censys.",
    tools: [
      {
        name: "Shodan CLI",
        command: "shodan search apache country:US",
        link: "https://cli.shodan.io/"
      },
      {
        name: "Censys",
        command: "https://search.censys.io/",
        link: "https://search.censys.io/"
      }
    ]
  },
  {
    title: "Google Dorks",
    description: "Use advanced Google search queries to find sensitive information.",
    tools: [
      {
        name: "Index of",
        command: 'site:example.com intitle:"index of"',
        link: "https://www.exploit-db.com/google-hacking-database"
      },
      {
        name: "Filetype PDF",
        command: "site:example.com filetype:pdf",
        link: "https://www.exploit-db.com/google-hacking-database"
      },
      {
        name: "Login Pages",
        command: "inurl:login site:example.com",
        link: "https://www.exploit-db.com/google-hacking-database"
      }
    ]
  }
];

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Copied to clipboard." });
  } catch {
    toast({ title: "Copy Failed", description: "Failed to copy.", variant: "destructive" });
  }
};

export const OSINTToolkitPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [tools, setTools] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch('/data/osint-tools.json')
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        
        // Check if response has content
        const text = await res.text();
        if (!text || text.trim() === '') {
          throw new Error("Empty response from server");
        }
        
        // Try to parse JSON
        return JSON.parse(text);
      })
      .then((allTools) => {
        // Only show approved tools
        allTools = allTools.filter((t: any) => t.approved !== false);
        let filtered = allTools;
        if (category) filtered = filtered.filter((t: any) => t.category === category);
        if (q) {
          const qStr = q.toLowerCase();
          filtered = filtered.filter((t: any) =>
            t.name.toLowerCase().includes(qStr) ||
            t.description.toLowerCase().includes(qStr) ||
            t.category.toLowerCase().includes(qStr)
          );
        }
        setTools(filtered);
        setAllCategories([...new Set(allTools.map((t: any) => t.category))]);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error loading OSINT tools:', error);
        setError('Failed to load tools');
        setLoading(false);
      });
  }, [category, q]);

  // Group tools by category
  const groupedTools = tools.reduce((acc, tool) => {
    const cleanCategory = tool.category.replace(/\[↑\]\([^)]*\)\s*/, '');
    if (!acc[cleanCategory]) {
      acc[cleanCategory] = [];
    }
    acc[cleanCategory].push(tool);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/toolkits')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/toolkits')} className="font-['Space_Grotesk'] text-black hidden lg:flex">Back to Toolkits</Button>
              <div onClick={() => {}} className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"><img className="w-6 h-6" alt="User" src="/user-icon.svg" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Mobile back button */}
          <div className="lg:hidden mb-4">
            <button 
              onClick={() => navigate('/toolkits')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
            >
              ← Back to Toolkits
            </button>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#3b82f6] rounded-lg flex items-center justify-center">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">OSINT Toolkit</h1>
              <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">A collection of essential OSINT tools, commands, and links for reconnaissance and information gathering.</p>
            </div>
          </div>

          {/* Filters and View Toggle */}
          <div className="flex flex-wrap gap-4 mb-8 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <input
                type="text"
                placeholder="Search tools..."
                value={q}
                onChange={e => setQ(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
              />
              <select value={category} onChange={e => setCategory(e.target.value)} className="border rounded px-3 py-2 text-sm">
                <option value="">All Categories</option>
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.replace(/\[↑\]\([^)]*\)\s*/, '')}
                  </option>
                ))}
              </select>
              {(category || q) && (
                <Button variant="outline" onClick={() => { setCategory(''); setQ(''); }}>Clear Filters</Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'cards' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="flex items-center gap-2"
              >
                <Grid className="w-4 h-4" />
                Cards
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="flex items-center gap-2"
              >
                <List className="w-4 h-4" />
                Table
              </Button>
            </div>
          </div>

          {loading && <div className="text-gray-500">Loading tools...</div>}
          {error && <div className="text-red-500">{error}</div>}

          {/* Card View */}
          {viewMode === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tools.map((tool, idx) => (
                <Card key={idx} className="mb-4 border-2 border-[#E7E7E7]">
                  <CardHeader>
                    <CardTitle className="text-xl text-black flex items-center gap-2">
                      <Badge variant="outline" className="text-[#b2c800] border-[#a0a000]">
                        {tool.category.replace(/\[↑\]\([^)]*\)\s*/, '')}
                      </Badge>
                    </CardTitle>
                    <p className="text-gray-600 mt-2">{tool.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="font-semibold text-black mb-1">{tool.name}</div>
                    <div className="flex items-center gap-2 mb-2">
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(tool.link)}><Copy className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" asChild><a href={tool.link} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && tools.length === 0 && <div className="text-gray-500 col-span-2">No tools found.</div>}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="space-y-8">
              {Object.entries(groupedTools).map(([categoryName, categoryTools]) => (
                <div key={categoryName}>
                  <h3 className="text-lg font-semibold text-black mb-4 border-b pb-2">
                    {categoryName} ({categoryTools.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-3 border">Tool Name</th>
                          <th className="text-left p-3 border">Description</th>
                          <th className="text-center p-3 border">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryTools.map((tool, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 border font-medium">{tool.name}</td>
                            <td className="p-3 border text-sm">{tool.description}</td>
                            <td className="p-3 border text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => copyToClipboard(tool.link)}><Copy className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" asChild><a href={tool.link} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {!loading && Object.keys(groupedTools).length === 0 && <div className="text-gray-500 text-center">No tools found.</div>}
            </div>
          )}

          {/* Attribution */}
          <div className="text-xs text-gray-500 mt-8 text-center">
            Tool data sourced from <a href="https://github.com/jivoi/awesome-osint" target="_blank" rel="noopener noreferrer" className="underline">awesome-osint</a> by jivoi (MIT License).
          </div>
        </div>
      </div>
    </div>
  );
}; 